import { getLigneOfDossier } from '@mgdis/demande-financement';
import { DemandesFinancementActions, DemandesFinancementMessageType, DemandesFinancementStatus, DemandesFinancementVirtualStatus, DossierFinancementDecisionType, DossierFinancementEventType, DossierFinancementStatus, JustificationStatus, } from '../types';
/**
 * DemandesFinancement formatter
 */
class DemandesFinancementFormatter {
    i18nService;
    sollicitationsFormatter;
    dossiersFinancementService;
    dateFormatterService;
    constructor({ i18nService, sollicitationsFormatter, dossiersFinancementService, dateFormatterService }) {
        this.i18nService = i18nService;
        this.sollicitationsFormatter = sollicitationsFormatter;
        this.dossiersFinancementService = dossiersFinancementService;
        this.dateFormatterService = dateFormatterService;
    }
    /**
     * generate and push an action to the dest array
     */
    formatAction(type, icon, extend = {}) {
        return {
            id: type,
            title: this.i18nService.translate(`demandes-financement.${type.split(':')[0]}`),
            icon: { icon },
            ...extend,
        };
    }
    /**
     * generate and push a tag to the dest array
     */
    formatTag(type, icon, badge = -1) {
        return {
            id: type,
            label: this.i18nService.translate(`demandes-financement.${type}`),
            icon: { icon },
            // display badge if equal or superior to 0
            ...(badge === -1
                ? {}
                : { badge: { value: badge, label: this.i18nService.translate('demandes-financement.share-label') } }),
        };
    }
    /**
     * Generate tag when demande is restricted
     */
    generateRestrictedTag() {
        return this.formatTag('restricted-access', 'lock');
    }
    /**
     * Check if dossier has instruction with avis defavorable
     */
    isDossierInstructionTermine(dossierFinancement) {
        const avisInstruction = (dossierFinancement.history?.events ?? []).filter((event) => event.type === DossierFinancementEventType.AVIS_INSTRUCTION);
        const isLastAvisDefavorable = avisInstruction?.at(-1)?.avis === DossierFinancementDecisionType.DEFAVORABLE;
        return dossierFinancement.statut === DossierFinancementStatus.FININSTRUCTION && isLastAvisDefavorable;
    }
    /**
     * format a demandes list to be used by a front component
     */
    formatDemandesFinancementList(demandes, currentUser, restrictedDemandes, publicSettings) {
        return Promise.all(demandes.map((demande) => this.formatDemandeFinancement(demande, currentUser, restrictedDemandes ?? [], publicSettings)));
    }
    /**
     * Build create payment extend
     */
    buildCreatePaymentExtend(action) {
        const extend = {
            disabled: !action?.enable,
        };
        if (extend.disabled) {
            extend.hoverMessage = this.i18nService.translate('demandes-financement.payment-no-remaining-account');
        }
        return extend;
    }
    /**
     * Build demande-financement actions
     * * this function mutates result parameter
     */
    buildDemandeFinancementActions(demandeFinancement, result) {
        // Actions
        if (demandeFinancement.primaryActions)
            for (const action of demandeFinancement.primaryActions) {
                switch (action.type) {
                    // RESUME
                    case DemandesFinancementActions.RESUME: {
                        result.actions.push(this.formatAction(DemandesFinancementActions.RESUME, 'pen'));
                        break;
                    }
                    // ACCESS
                    case DemandesFinancementActions.ACCESS: {
                        result.actions.push(this.formatAction(DemandesFinancementActions.ACCESS, 'chevron-right', {
                            variant: 'secondary',
                        }));
                        break;
                    }
                    case DemandesFinancementActions.CREATE_PAYMENT: {
                        result.actions.unshift(this.formatAction(DemandesFinancementActions.CREATE_PAYMENT, 'hand-up', this.buildCreatePaymentExtend(action)));
                        break;
                    }
                    case DemandesFinancementActions.ACCESS_ALL_PAYMENTS: {
                        result.actions.unshift(this.formatAction(DemandesFinancementActions.ACCESS_ALL_PAYMENTS, 'euro-circle'));
                        break;
                    }
                }
            }
    }
    /**
     * Build demande-financement sub actions
     * * this function mutates result parameter
     */
    buildDemandeFinancementSubActions(demandeFinancement, result, publicSettings) {
        // Sub Actions
        if (demandeFinancement.secondaryActions)
            for (const action of demandeFinancement.secondaryActions) {
                switch (action.type) {
                    // SHARE
                    case DemandesFinancementActions.SHARE: {
                        result.subactions?.push(this.formatAction(DemandesFinancementActions.SHARE, 'user-plus', { variant: 'flat' }));
                        break;
                    }
                    // DELETE
                    case DemandesFinancementActions.DELETE: {
                        result.subactions?.push(this.formatAction(DemandesFinancementActions.DELETE, 'trash', { variant: 'flat' }));
                        break;
                    }
                    // MANAGE_RIGHTS
                    case DemandesFinancementActions.MANAGE_RIGHTS: {
                        result.subactions?.push(this.formatAction(DemandesFinancementActions.MANAGE_RIGHTS, 'unlock', { variant: 'flat' }));
                        break;
                    }
                    // CONTRIBUTION_INFORMATION
                    case DemandesFinancementActions.CONTRIBUTION_INFORMATION: {
                        result.subactions?.push({
                            id: `${DemandesFinancementActions.CONTRIBUTION_INFORMATION}:${action.href}`,
                            title: action.value,
                            icon: { icon: 'pen' },
                            href: action.href,
                        });
                        break;
                    }
                    // ADD_PIECES
                    case DemandesFinancementActions.ADD_PIECES: {
                        if (!publicSettings.hideDemandeDepotPieces) {
                            result.subactions?.push(this.formatAction(DemandesFinancementActions.ADD_PIECES, 'file-upload', { variant: 'flat' }));
                        }
                        break;
                    }
                    // DUPLICATE
                    case DemandesFinancementActions.DUPLICATE: {
                        result.subactions?.push(this.formatAction(DemandesFinancementActions.DUPLICATE, 'copy', { variant: 'flat' }));
                        break;
                    }
                    // ACCESS_PAYMENTS
                    case DemandesFinancementActions.ACCESS_PAYMENTS: {
                        result.subactions?.push(this.formatAction(`${DemandesFinancementActions.ACCESS_PAYMENTS}:${action.value}`, 'euro-circle', {
                            subTitle: action.value,
                        }));
                        break;
                    }
                }
            }
    }
    /**
     * Build dossiers financement events
     * * this function mutates result parameter
     */
    async addDossiersFinancementEvents(demandeFinancement, result) {
        // For every dossier financement we check if it's voted then we add the date of the enrolement in the history
        if (demandeFinancement.dossiersFinancement) {
            const datesDossiersFinancement = [];
            for (const dossierFinancement of demandeFinancement.dossiersFinancement) {
                if ([DossierFinancementStatus.VOTE, DossierFinancementStatus.SOLDE].includes(dossierFinancement?.statut)) {
                    // build datesDossiersFinancement
                    for (const event of dossierFinancement?.history?.events ?? []) {
                        if (event.type === DossierFinancementEventType.DECISION) {
                            datesDossiersFinancement.push(event.dateMetier ?? event.date);
                        }
                    }
                }
            }
            if (datesDossiersFinancement.length > 0) {
                //get latest decision date
                // eslint-disable-next-line unicorn/no-array-reduce
                const dateLastDecision = datesDossiersFinancement.reduce((maxDate, date) => (maxDate > date ? maxDate : date));
                if (result.historydetails.length === 3) {
                    result.historydetails[2] = await this.translateEventDate('demandes-financement.voted-at', dateLastDecision, false);
                }
                else
                    result.historydetails.push(await this.translateEventDate('demandes-financement.voted-at', dateLastDecision, false));
            }
        }
    }
    /**
     * Build dossiers financement events
     * * this function mutates result parameter
     */
    async addJustificationEvents(demandeFinancement, result) {
        // Add date when demande is justified in history
        if ('justification' in demandeFinancement &&
            demandeFinancement.justification?.statut === JustificationStatus.justified) {
            const datesJustified = [];
            if (demandeFinancement.justification?.history?.events)
                for (const event of demandeFinancement.justification?.history?.events ?? []) {
                    if (event.reference === JustificationStatus.justified) {
                        datesJustified.push(event.date);
                    }
                }
            if (datesJustified.length > 0) {
                // get latest justification date
                // eslint-disable-next-line unicorn/no-array-reduce
                const dateLastJustified = datesJustified.reduce((maxDate, date) => (maxDate > date ? maxDate : date));
                result.historydetails.push(await this.translateEventDate('demandes-financement.justified-at', dateLastJustified));
            }
        }
    }
    async translateEventDate(key, date, includeTime = true) {
        try {
            const translatedDate = this.i18nService.translate(key, {
                date: includeTime
                    ? this.dateFormatterService.asDatetime(date, { style: 'full' })
                    : this.dateFormatterService.asDate(date, { style: 'full' }),
            });
            return translatedDate;
        }
        catch (error) {
            console.error(error);
            const hasDatePattern = new RegExp('\\d{4}-\\d{2}-\\d{2}');
            return hasDatePattern.test(date) ? this.translateEventDate(key, new Date(date).toISOString()) : '';
        }
    }
    /**
     * Build history
     * * this function mutates result parameter
     */
    async buildHistory(demandeFinancement, result) {
        // history details
        let requestedEventAlreadyHandled = false;
        if (demandeFinancement.history?.events) {
            // add creation date
            if (demandeFinancement.history.events[0]?.reference === DemandesFinancementStatus.supported) {
                const translateText = demandeFinancement.idCpo
                    ? 'demandes-financement.requested-and-supported-at'
                    : 'demandes-financement.requested-at';
                result.historydetails.push(await this.translateEventDate(translateText, demandeFinancement.history.begin?.date ?? '', demandeFinancement.virtualStatus !== DemandesFinancementVirtualStatus.cloture));
                requestedEventAlreadyHandled = true;
            }
            // push events history
            for (const event of demandeFinancement.history.events) {
                switch (event.reference) {
                    // REQUESTED
                    case DemandesFinancementStatus.requested: {
                        if (!requestedEventAlreadyHandled) {
                            result.historydetails.push(await this.translateEventDate('demandes-financement.requested-at', demandeFinancement.history.begin?.date ?? ''));
                            requestedEventAlreadyHandled = true;
                        }
                        break;
                    }
                    // REGISTERED
                    case DemandesFinancementStatus.registered: {
                        result.historydetails.push(await this.translateEventDate('demandes-financement.transmitted-at', event.date));
                        break;
                    }
                    // TRANSMITTED
                    case DemandesFinancementStatus.transmitted: {
                        demandeFinancement.avisPrealables && demandeFinancement.avisPrealables.length > 0
                            ? result.historydetails.push(await this.translateEventDate('demandes-financement.registered-at', event.date))
                            : result.historydetails.push(await this.translateEventDate('demandes-financement.transmitted-at', event.date));
                        break;
                    }
                    // SUPPORTED
                    case DemandesFinancementStatus.supported: {
                        if (result.historydetails.length === 3)
                            result.historydetails[2] = await this.translateEventDate('demandes-financement.supported-at', event.date, demandeFinancement.virtualStatus !== DemandesFinancementVirtualStatus.cloture);
                        else
                            result.historydetails.push(await this.translateEventDate('demandes-financement.supported-at', event.date, demandeFinancement.virtualStatus !== DemandesFinancementVirtualStatus.cloture));
                    }
                }
            }
        }
        await this.addDossiersFinancementEvents(demandeFinancement, result);
        await this.addJustificationEvents(demandeFinancement, result);
        // reverse historydetails entries to display history in anti-chronological order
        result.historydetails.reverse();
    }
    /**
     * Build dossiers financement
     * * this function mutates result parameter
     */
    buildStatus(demandeFinancement, result) {
        result.status = this.i18nService.translate(`connected.dashboard.aides.list.state-${demandeFinancement.virtualStatus}`);
        if (Array.isArray(demandeFinancement.dossiersFinancement)) {
            const countDossierInstructionTermine = demandeFinancement.dossiersFinancement.filter((dossierFinancement) => dossierFinancement && this.isDossierInstructionTermine(dossierFinancement)).length;
            if (countDossierInstructionTermine === demandeFinancement.dossiersFinancement.length &&
                demandeFinancement.virtualStatus === DemandesFinancementVirtualStatus.finInstruction)
                result.status = this.i18nService.translate(`connected.dashboard.aides.list.state-${demandeFinancement.virtualStatus}_DEFAVORABLE`);
        }
        // do not display PRET A VOTER status for final user
        if (demandeFinancement.virtualStatus === DemandesFinancementVirtualStatus.pretAVoter) {
            result.status = this.i18nService.translate(`connected.dashboard.aides.list.state-INSTRUCTION`);
        }
    }
    /**
     * Build dossiers financement
     * * this function mutates result parameter
     */
    buildDossiersFinancement(demandeFinancement, result) {
        // For every dossier format them
        result.dossiersFinancement = demandeFinancement.dossiersFinancement?.map((dossierFinancement) => {
            const actions = [];
            const prefix = this.i18nService.translate('demandes-financement.dossier');
            // For each dossierFinancement, we check if we have the action create-payment and format it and add in the array actions
            if (dossierFinancement.actions)
                for (const action of dossierFinancement.actions) {
                    // CREATE_PAYMENT
                    if (action.type === DemandesFinancementActions.CREATE_PAYMENT) {
                        actions.push(this.formatAction(DemandesFinancementActions.CREATE_PAYMENT, 'hand-up', this.buildCreatePaymentExtend(action)));
                    }
                    else if (action.type === DemandesFinancementActions.ACCESS_ALL_PAYMENTS) {
                        // in the case of multi-dossiers, we add the reference administrative of the dossier in order to find it later
                        actions.push(this.formatAction(`${DemandesFinancementActions.ACCESS_ALL_PAYMENTS}:${dossierFinancement.referenceAdministrative}`, 'euro-circle'));
                    }
                }
            const lignePlanFinancement = getLigneOfDossier(demandeFinancement, dossierFinancement.href) ?? {};
            const hasAvenant = this.hasAvenantInDossier(lignePlanFinancement, dossierFinancement);
            let statut;
            let statutInstructionDefavorable = false;
            if (dossierFinancement)
                statutInstructionDefavorable = this.isDossierInstructionTermine(dossierFinancement);
            if (statutInstructionDefavorable) {
                statut = this.i18nService.translate(`connected.dashboard.aides.list.dossiersFinancement.states.FININSTRUCTION_DEFAVORABLE`);
            }
            else if (hasAvenant && dossierFinancement.statut !== DossierFinancementStatus.SOLDE) {
                statut = this.i18nService.translate(`connected.dashboard.aides.list.dossiersFinancement.states.VOTE`);
            }
            else {
                statut = this.i18nService.translate(`connected.dashboard.aides.list.dossiersFinancement.states.${dossierFinancement.statut}`);
            }
            return {
                title: `${prefix} ${dossierFinancement.referenceAdministrative} ${dossierFinancement.libelle}`,
                statut,
                actions,
                reference: dossierFinancement.reference,
                referenceAdministrative: dossierFinancement.referenceAdministrative,
                teleservicePaiement: dossierFinancement.dispositif?.expand?.teleservicePaiement?.href?.split('/')?.pop(),
                href: dossierFinancement.href,
                hasOngoingPayment: dossierFinancement.hasOngoingPayment,
            };
        });
    }
    addMessage(result, message) {
        if (!message.link)
            delete message.link;
        if (!result.messages)
            result.messages = [];
        result.messages.push(message);
    }
    /**
     * Check if we have at least one dossier voted
     *
     * @param dossiersFinancement - dossiers Financment
     * @returns dossiers financement
     */
    getDossierVoted(dossiersFinancement) {
        return dossiersFinancement.filter((dossierFinancement) => {
            return (dossierFinancement?.statut &&
                [DossierFinancementStatus.VOTE, DossierFinancementStatus.SOLDE].includes(dossierFinancement.statut));
        });
    }
    isExternalDossierFinancement(dossierFinancementHRef) {
        return !dossierFinancementHRef?.startsWith('/referentiel-dossiers-financement/');
    }
    /**
     * Check if we have avenant on the dossier
     * @returns true if dossier has at least one avenant, false otherwise
     */
    hasAvenantInDossier(ligne, dossierFinancement) {
        // if dossier is v9 we have a specific property for avenant
        return this.isExternalDossierFinancement(dossierFinancement.href)
            ? this.hasAvenantInPlanFinancementV8(ligne)
            : !!dossierFinancement.avenant?.id;
    }
    /**
     * Check if dossier v8 has avenant that are not canceled
     * via plan financement
     */
    hasAvenantInPlanFinancementV8(ligne) {
        const events = ligne.financement?.history?.events ?? [];
        //In v8 We consider having an event when we have more than one decision
        const decisions = this.dossiersFinancementService.getActiveDecisionsV8(events);
        return decisions.length > 1;
    }
    hasMultiDossierDemandeAnyDecisionFavorable(demandeFinancement, dossiersFinancement) {
        return dossiersFinancement.some((dossierFinancement) => {
            const dossierFinancementId = dossierFinancement?.href;
            const lignePlanFinancement = getLigneOfDossier(demandeFinancement, dossierFinancementId) ?? {};
            return this.isExternalDossierFinancement(dossierFinancementId)
                ? this.dossiersFinancementService.isAideHaveAtLeastOneActiveDecisionFavorableV8(lignePlanFinancement)
                : this.dossiersFinancementService.isAideHaveAtLeastOneActiveDecisionFavorable(lignePlanFinancement);
        });
    }
    hasMonoDossierDemandeAnyDecisionFavorable(demandeFinancement, dossierFinancement) {
        const dossierFinancementId = dossierFinancement.href;
        const lignePlanFinancement = getLigneOfDossier(demandeFinancement, dossierFinancementId) ?? {};
        return this.isExternalDossierFinancement(dossierFinancementId)
            ? this.dossiersFinancementService.isAideHaveAtLeastOneActiveDecisionFavorableV8(lignePlanFinancement)
            : this.dossiersFinancementService.isAideHaveAtLeastOneActiveDecisionFavorable(lignePlanFinancement);
    }
    /**
     * It will retrieve informations on dossier such as
     * if it has at least one avenant
     * if it has demande paiement
     * if it has ongoing payment
     * and if the action "create-payment" exist on the dossier
     */
    checkInfoInDossiersToDisplayMessage(demandeFinancement) {
        let hasAtLeastOneDossierWithAvenant = false;
        let hasDemandePaiement = false;
        let hasOngoingPayment = false;
        let hasCreatePaymentAction = false;
        for (const dossierFinancement of demandeFinancement.dossiersFinancement ?? []) {
            const lignePlanFinancement = getLigneOfDossier(demandeFinancement, dossierFinancement?.href) ?? {};
            if (dossierFinancement) {
                hasAtLeastOneDossierWithAvenant ||= this.hasAvenantInDossier(lignePlanFinancement, dossierFinancement);
                hasDemandePaiement ||= Number(dossierFinancement.demandesPaiement?.length) > 0;
                hasOngoingPayment ||= !!dossierFinancement.hasOngoingPayment;
                hasCreatePaymentAction ||= !!dossierFinancement.actions?.some((action) => action.type === 'create-payment' && action.enable === true);
            }
            if (hasAtLeastOneDossierWithAvenant && hasDemandePaiement && hasOngoingPayment && hasCreatePaymentAction)
                break;
        }
        return { hasAtLeastOneDossierWithAvenant, hasDemandePaiement, hasOngoingPayment, hasCreatePaymentAction };
    }
    /**
     * Add messages in demandeFinancement for dossiers states
     */
    buildMessagesForMultiDossier(demandeFinancement, result, dossiersVoted, hasAvenant) {
        let dossierLabel;
        let dossierVariant;
        let dossierLink = '';
        // We check if at least one dossier has been voted, involving a different message
        if (dossiersVoted.length > 0) {
            const hasAtLeastOneDossierWithDecisionFavorable = this.hasMultiDossierDemandeAnyDecisionFavorable(demandeFinancement, dossiersVoted);
            // If at least one dossier has a decision favorable we have a specific message
            if (hasAtLeastOneDossierWithDecisionFavorable) {
                dossierLink = this.i18nService.translate('demandes-financement.see-decisions');
                // In case of avenant we display a specific message
                if (hasAvenant) {
                    dossierLabel = this.i18nService.translate('demandes-financement.dossiers-voted-message');
                    dossierVariant = 'info';
                }
                else {
                    dossierLabel = this.i18nService.translate('demandes-financement.granted-demande');
                    dossierVariant = 'success';
                }
            }
            else {
                dossierLabel = this.i18nService.translate('demandes-financement.dossiers-voted-message');
                dossierVariant = 'info';
            }
        }
        // Specific message when dossiers are not voted yet
        else {
            dossierLabel = this.i18nService.translate('demandes-financement.multi-dossiers-message');
            dossierVariant = 'info';
        }
        this.addMessage(result, {
            label: dossierLabel,
            link: dossierLink,
            toggleOpened: this.i18nService.translate('demandes-financement.history-toggle-opened-label'),
            toggleClosed: this.i18nService.translate('demandes-financement.history-toggle-closed-label'),
            variant: dossierVariant,
            type: DemandesFinancementMessageType.DOSSIERS_FINANCEMENT,
            displayDetails: true,
        });
    }
    buildMessagesForMonoDossier(demandeFinancement, result, dossiersVoted, hasAvenant) {
        let dossierHasDecisionFavorable;
        // Check if dossier has been voted to then check the decisions and display a specific message
        if (dossiersVoted.length > 0 && dossiersVoted[0]) {
            let dossierLabel = this.i18nService.translate('demandes-financement.dossiers-voted-message');
            let dossierVariant = 'info';
            dossierHasDecisionFavorable = this.hasMonoDossierDemandeAnyDecisionFavorable(demandeFinancement, dossiersVoted[0]);
            if (dossierHasDecisionFavorable && !hasAvenant) {
                dossierLabel = this.i18nService.translate('demandes-financement.granted-demande');
                dossierVariant = 'success';
            }
            this.addMessage(result, {
                label: dossierLabel,
                link: dossierHasDecisionFavorable ? this.i18nService.translate('demandes-financement.see-decisions') : '',
                variant: dossierVariant,
                type: DemandesFinancementMessageType.DOSSIERS_FINANCEMENT,
                displayDetails: false,
            });
        }
    }
    buildMessagesForDossiers(demandeFinancement, result) {
        let dossierLabel = '';
        let dossierVariant = '';
        const dossierLink = '';
        const { hasAtLeastOneDossierWithAvenant, hasDemandePaiement, hasOngoingPayment, hasCreatePaymentAction } = this.checkInfoInDossiersToDisplayMessage(demandeFinancement);
        const isMultiDossierWithPaiementAndCreateAction = Number(demandeFinancement.dossiersFinancement?.length) > 1 && hasCreatePaymentAction && hasDemandePaiement;
        // Case if at least one dossier has on goinf payment, or if it matches RG :
        if (hasOngoingPayment || isMultiDossierWithPaiementAndCreateAction) {
            dossierLabel = this.i18nService.translate('demandes-financement.ongoing-payments');
            dossierVariant = 'info';
        }
        if (hasOngoingPayment || isMultiDossierWithPaiementAndCreateAction) {
            this.addMessage(result, {
                label: dossierLabel,
                link: dossierLink,
                toggleOpened: this.i18nService.translate('demandes-financement.history-toggle-opened-label'),
                toggleClosed: this.i18nService.translate('demandes-financement.history-toggle-closed-label'),
                variant: dossierVariant,
                type: DemandesFinancementMessageType.DOSSIERS_FINANCEMENT,
                displayDetails: Number(demandeFinancement.dossiersFinancement?.length) > 1,
            });
        }
        else if (!hasDemandePaiement &&
            Array.isArray(demandeFinancement.dossiersFinancement) &&
            demandeFinancement.dossiersFinancement.length > 0) {
            const dossiersVoted = this.getDossierVoted(demandeFinancement.dossiersFinancement);
            // Multi-dossier case
            if (demandeFinancement.dossiersFinancement.length > 1) {
                this.buildMessagesForMultiDossier(demandeFinancement, result, dossiersVoted, hasAtLeastOneDossierWithAvenant);
            }
            // Mono-dossier case
            else {
                this.buildMessagesForMonoDossier(demandeFinancement, result, dossiersVoted, hasAtLeastOneDossierWithAvenant);
            }
        }
    }
    formatTitle(demandeFinancement) {
        let title = demandeFinancement.title || '';
        // title reference administrative
        if (demandeFinancement.referenceAdministrative) {
            title += ` (${demandeFinancement.referenceAdministrative})`;
        }
        return title;
    }
    /**
     * format a demande to be used by a front component
     */
    async formatDemandeFinancement(demandeFinancement, currentUser, restrictedDemandes, publicSettings) {
        const result = {
            id: demandeFinancement.id,
            title: demandeFinancement.title,
            actions: [],
            subactions: [],
            history: { begin: demandeFinancement.history.begin },
            historydetails: [],
            status: '',
            statusKey: demandeFinancement.status,
            tags: [],
            subActionsBtnLabel: '',
            reference: demandeFinancement.reference,
            teleservice: demandeFinancement.teleservice,
            demandeur: demandeFinancement.demandeur,
            signataire: demandeFinancement.signataire,
            isRestricted: false,
        };
        // set awaiting actions
        if (currentUser && demandeFinancement.awaitingActions && demandeFinancement.awaitingActions.length > 0) {
            result.awaitingActions = this.sollicitationsFormatter.formatAwaitingActions(demandeFinancement.awaitingActions, currentUser);
        }
        if (demandeFinancement.multiFinanceur !== undefined)
            result.multiFinanceur = demandeFinancement.multiFinanceur;
        if (demandeFinancement.multiFinancement)
            result.multiFinancement = demandeFinancement.multiFinancement;
        if (demandeFinancement.financeurPrincipal)
            result.financeurPrincipal = demandeFinancement.financeurPrincipal;
        if (demandeFinancement.planFinancement)
            result.planFinancement = demandeFinancement.planFinancement;
        if (demandeFinancement.beneficiaires)
            result.beneficiaires = demandeFinancement.beneficiaires;
        if (demandeFinancement.cloture)
            result.cloture = demandeFinancement.cloture;
        this.buildDemandeFinancementActions(demandeFinancement, result);
        this.buildDemandeFinancementSubActions(demandeFinancement, result, publicSettings);
        await this.buildHistory(demandeFinancement, result);
        // 1) message for awaiting actions
        if (result.awaitingActions && result.awaitingActions.length > 0) {
            this.addMessage(result, {
                label: this.i18nService.translate('demandes-financement.more-actions-needed-message'),
                toggleOpened: this.i18nService.translate('demandes-financement.history-toggle-opened-label'),
                toggleClosed: this.i18nService.translate('demandes-financement.history-toggle-closed-label'),
                variant: 'warning',
                type: DemandesFinancementMessageType.AWAITING_ACTIONS,
                displayDetails: true,
            });
        }
        // 2) message for dossier
        this.buildMessagesForDossiers(demandeFinancement, result);
        this.buildDossiersFinancement(demandeFinancement, result);
        // status
        this.buildStatus(demandeFinancement, result);
        // tags
        if (demandeFinancement.shareCount > 0) {
            result.tags.push(this.formatTag('shared', 'users', demandeFinancement.shareCount));
        }
        // idCpo
        if (demandeFinancement.idCpo) {
            result.tags.push(this.formatTag('linkedDemandes', 'link'));
            if (demandeFinancement.exerciceBudgetaire)
                result.title += ` | ${this.i18nService.translate('demandes-financement.year')} ${demandeFinancement.exerciceBudgetaire}`;
        }
        // sub actions menu label
        result.subActionsBtnLabel = this.i18nService.translate('demandes-financement.sub-actions-btn-label');
        // are demande restrictions activated ?
        result.isRestricted = restrictedDemandes.includes(demandeFinancement.id); // Needed for angularJS front component
        if (result.isRestricted)
            result.tags.push(this.generateRestrictedTag());
        result.title = this.formatTitle(demandeFinancement);
        return result;
    }
    /**
     * Deactivate share action button + display spinner
     * @param contribution - contribution to update
     */
    setDemandeFinancementAwaitingActionsOpened(demandeFinancement) {
        const message = demandeFinancement.messages?.find(({ type }) => type === 'awaiting-actions');
        if (message)
            message.expanded = true;
    }
    /**
     * Update 'share-contribution' type action in given contribution
     */
    updateContributionSharingsAction(contribution, sharings) {
        this.sollicitationsFormatter.updateContributionSharingsAction(contribution, sharings);
    }
    /**
     * Reactivate share action button + hide spinner
     * @param contribution - contribution to update
     */
    unsetShareContributionActionLoading(contribution) {
        this.sollicitationsFormatter.unsetShareContributionActionLoading(contribution);
    }
    /**
     * Reactivate share action button + hide spinner
     * @param justification - justification to update
     */
    unsetJustificationActionLoading(justification) {
        this.sollicitationsFormatter.unsetJustificationActionLoading(justification);
    }
}
export { DemandesFinancementFormatter };
//# sourceMappingURL=demandes-financement-formatter.js.map